import { useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import type { ApexOptions } from 'apexcharts';
import { useTranslation } from 'react-i18next';
import { chartTokens } from '../../design-system/chartTokens';
import { fonts } from '../../design-system/tokens';
import type { BDTheme } from '../../design-system/types';
import { getLanguage } from '../../util/languages';
import { fetchTimeline } from './api';
import { categoryColor } from './format';
import type { CategoryTimelineResponse, Period } from './types';

type Props = {
  operatorId: string;
  period: Period;
  cpeId?: number;
  devId?: number;
  categoryIds: string[];
  darkMode?: boolean;
};

// Buckets are UTC: 'YYYY-MM-DD HH:00:00' (hour) or 'YYYY-MM-DD' (day)
const label = (x: string, groupBy: 'hour' | 'day', locale: string) =>
  groupBy === 'hour'
    ? x.slice(11, 16)
    : new Date(`${x}T00:00:00Z`).toLocaleDateString(locale, { month: 'short', day: 'numeric', timeZone: 'UTC' });

export const CategoryTimeline = ({ operatorId, period, cpeId, devId, categoryIds, darkMode }: Props) => {
  const { t, i18n } = useTranslation();
  const theme: BDTheme = darkMode ? 'dark' : 'light';
  const tokens = chartTokens[theme];
  const locale = getLanguage(i18n.language).locale;

  const [data, setData] = useState<CategoryTimelineResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const idsKey = categoryIds.join(',');
  useEffect(() => {
    if (categoryIds.length === 0) {
      setData(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchTimeline(operatorId, { l: period, cpeId, devId, categoryIds })
      .then((res) => !cancelled && setData(res))
      .catch(() => !cancelled && setError(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // categoryIds is tracked through idsKey so a new array with the same ids does not refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operatorId, period, cpeId, devId, idsKey]);

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'area',
        stacked: true,
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: { enabled: false },
        background: 'transparent',
        foreColor: tokens.mute,
        fontFamily: fonts.sans,
      },
      theme: { mode: theme },
      colors: (data?.series || []).map((s) => categoryColor(s.id)),
      stroke: { curve: 'smooth', width: 2 },
      fill: { type: 'solid', opacity: 0.45 },
      dataLabels: { enabled: false },
      grid: { borderColor: tokens.border, strokeDashArray: 3 },
      legend: { position: 'bottom', labels: { colors: tokens.text2 } },
      xaxis: {
        categories: (data?.series[0]?.points || []).map((p) => label(p.x, data!.groupBy, locale)),
        tickAmount: 12,
        labels: { style: { colors: tokens.mute, fontSize: '11px' } },
      },
      yaxis: { labels: { style: { colors: tokens.mute, fontFamily: fonts.mono }, formatter: (v: number) => Math.round(v).toLocaleString(locale) } },
      tooltip: { shared: true, intersect: false },
    }),
    [data, locale, theme, tokens]
  );

  const series = (data?.series || []).map((s) => ({ name: s.name, data: s.points.map((p) => p.y) }));

  return (
    <section className="bd-card tm-chart">
      <div className="tm-chart-hd">
        <h2 className="bd-h3">{t('dnsCategories.chart.title')}</h2>
        <span className="bd-meta">{t('dnsCategories.chart.hint')}</span>
      </div>
      {categoryIds.length === 0 && <div className="dc-state bd-meta">{t('dnsCategories.chart.noSelection')}</div>}
      {error && <div className="dc-state bd-meta" role="alert">{t('dnsCategories.chart.error')}</div>}
      {loading && !data && !error && <div className="dc-state bd-meta">{t('dnsCategories.chart.loading')}</div>}
      {data && !error && categoryIds.length > 0 && (
        <ReactApexChart key={theme} options={options} series={series} type="area" height={430} />
      )}
    </section>
  );
};
